import React from "react";
import {
    Alert,
    Box, Button,
    Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl,
    Grid, Grow,
    IconButton,
    InputLabel,
    MenuItem,
    Select, Snackbar,
    TextField,
    Tooltip
} from "@mui/material";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import ClearIcon from '@mui/icons-material/Clear';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PropTypes from "prop-types";
import TableUtils from "../utils/TableUtils";
import CourseData from "../backend/dataservices/CourseData";
import User from "../backend/User";
import Config from "../utils/Config";
import FileSaver from "file-saver"
import axios from "axios";


export default function CoursesFilterAndReports(props) {

    const [filterYearValue, setFilterYearValue] = React.useState(null);
    const [filterDateValue, setFilterDateValue] = React.useState({name: "", id: ""});
    const [filterStateValue, setFilterStateValue] = React.useState("");
    const [availableDates, setAvailableDates] = React.useState([]);
    const [showReportDialog, setShowReportDialog] = React.useState(false);
    const [reportType, setReportType] = React.useState("");
    const [reportFileType, setReportFileType] = React.useState("");
    const [snackbarOpen, setSnackbarOpen] = React.useState(false);
    const [snackbarText, setSnackbarText] = React.useState("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState("success");

    React.useEffect(() => {
        clearFilter();
        (async () => {
            loadDates();
        })();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const reportTypes = [
        {name: "Teilnehmerliste"},
        {name: "Anwesenheitsliste"}
    ]

    const reportFileTypes = [
        {name: "PDF"},
        {name: "XLS"}
    ]

    function clearFilter() {
        clearDateFilter();
        clearYearFilter();
        clearStateFilter();
        thisApplyFilters();
    }


    function handleDateFilterChange(name, id) {
        thisApplyFilters(null, {name: name, id: id}, filterStateValue);
        setFilterDateValue({name: name, id: id});
    }

    function thisApplyFilters(year = null, date = {name: "", id: ""}, state = '') {
        props.applyFilters(year != null ? (year.getYear() + 1900).toString() : year, date, state);
    }

    function loadDates() {
        let tempDates = [];
        CourseData.fetchDates(props.course).then((dates) => {
            if(dates !== '') {
                dates.forEach(date => {
                    date._type = "attendance";
                    tempDates.push(date);
                });
                tempDates.sort((a, b) => a.KD_Anfangstag < b.KD_Anfangstag)
                setAvailableDates(tempDates);
            }
        });
    }

    function clearDateFilter() {
        setFilterDateValue({name: "", id: ""});
    }

    function clearYearFilter() {
        setFilterYearValue(null);
    }

    function clearStateFilter() {
        setFilterStateValue('');
    }

    function handleStateFilterChange(event) {
        setFilterStateValue(event.target.value);
    }

    function openDialog() {
        setReportType('');
        setReportFileType('');
        setShowReportDialog(true);
    }

    function closeDialog() {
        setShowReportDialog(false);
    }

    function createReport() {
        if(reportType === "Teilnehmerliste") {
            reportAttendees();
        } else if(reportType === "Anwesenheitsliste") {
            reportPresence();
        }
    }

    function closeSnackbar() {
        setSnackbarOpen(false);
    }

    function reportAttendees() {

        let {format, url} = createURL('TeilnehmerListe.');

        const fileName = 'Teilnehmer' + props.course.KS_Kursname + filterDateValue.name;
        return createFile(url, format, fileName);
    }

    function reportPresence() {

        let {format, url} = createURL('Anwesenheitsliste.');

        const fileName = 'Anwesenheit' + props.course.KS_Kursname + filterDateValue.name;
        return createFile(url, format, fileName);
    }

    function createURL(name) {
        let format = reportFileType.toLowerCase();

        let url = Config.reportingServerUrl + name;
        url += format;
        url += '?JWT='
        url += User.getToken();
        url += '&TerminID=' + filterDateValue.id;
        return {format, url};
    }

    function createFile(url, format, fileName) {
        return axios.get(Config.baseURL + url, {
            responseType: 'arraybuffer',
        }).then(function success(response) {
            let file;
            if (format === 'pdf') {
                file = new Blob([response.data], {type: 'application/pdf'});
                FileSaver.saveAs(file, fileName + '.pdf');
            } else if (format === 'xls') {
                file = new Blob([response.data], {type: 'application/vnd.ms-excel'});
                FileSaver.saveAs(file, fileName + '.xls');
            }
        }, function error(response) {
            setSnackbarSeverity('error');
            setSnackbarText("Fehler beim generieren des Reports");
            setSnackbarOpen(true);
        });
    }

    return (
        <Box>
            <Grid container spacing={2} alignItems={'center'} justifyContent={'end'}>
                <Grid item xs={2}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            views={['year']}
                            label={"Jahr"}
                            onChange={(newYear) => {
                                setFilterYearValue(newYear);
                                clearDateFilter();
                                thisApplyFilters(newYear, {name: "", id: ""}, filterStateValue);
                            }} value={filterYearValue} renderInput={(params) => {
                            return <TextField {...params} variant={"standard"}/>
                        }}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item xs={4}>
                    <FormControl fullWidth variant={"standard"}>
                        <InputLabel id={"date_filter"}>Termin</InputLabel>
                        <Select labelId={"date_filter"}
                                value={filterDateValue.name}
                                label={"Termin"}
                                onChange={(event) => {
                                    clearYearFilter();
                                }}>

                            {availableDates.map(function (value) {
                                return <MenuItem key={value.KD_ID}
                                                 onClick={() => handleDateFilterChange(TableUtils.dateFormatter(value), value.KD_ID)}
                                                 value={TableUtils.dateFormatter(value)}>{TableUtils.dateFormatter(value)}</MenuItem>
                            })}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={3}>
                    <FormControl fullWidth variant={"standard"}>
                        <InputLabel id={"status_filter"}>Kursstatus</InputLabel>
                        <Select labelId={"status_filter"}
                                value={filterStateValue}
                                label={"Kursstatus"}
                                onChange={(event) => {
                                    handleStateFilterChange(event);
                                    thisApplyFilters(filterYearValue, filterDateValue, event.target.value);
                                }}>
                            {TableUtils.states.map(function (value) {
                                return <MenuItem
                                    key={value.name} value={value.name}>{value.name}</MenuItem>
                            })}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={1}>
                    <Box>
                        <Tooltip title={"Filter zurücksetzen"}>
                            <IconButton onClick={() => {
                                clearFilter();
                            }}>
                                <ClearIcon/>
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Grid>
                {User.getRole() === 'Verwalter' ?
                    <Grid item xs={1}>
                        <Box>
                            <Tooltip
                                title={filterDateValue.name === '' ? 'Bitte Termin wählen um Report erstellen zu können' : 'Report erstellen'}>
                                <span>
                                    <IconButton color={'primary'} onClick={() => {
                                        openDialog();
                                    }} size={'large'}
                                                disabled={filterDateValue.name === ''}>
                                        <AssessmentIcon fontSize={'large'}/>
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    </Grid>
                    :
                    null
                }
            </Grid>

            <Dialog
            open={showReportDialog}
            onClose={closeDialog}
            fullWidth
            keepMounted>
                <DialogTitle sx={{backgroundColor: 'rgb(0,150,136)'}} color={"white"}>
                    Report erstellen
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} marginTop={1}>
                        <Grid item xs={6}>
                            <FormControl fullWidth variant={"standard"}>
                                <InputLabel id={"report_type"}>Report</InputLabel>
                                <Select labelId={"report_type"}
                                        value={reportType}
                                        label={"Report"}
                                        onChange={(event) => {
                                            setReportType(event.target.value)
                                        }}>
                                    {reportTypes.map(function (value) {
                                        return <MenuItem
                                            key={value.name} value={value.name}>{value.name}</MenuItem>
                                    })}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl fullWidth variant={"standard"}>
                                <InputLabel id={"report_file_type"}>Format</InputLabel>
                                <Select labelId={"report_file_type"}
                                        value={reportFileType}
                                        label={"Format"}
                                        onChange={(event) => {
                                            setReportFileType(event.target.value)
                                        }}>
                                    {reportFileTypes.map(function (value) {
                                        return <MenuItem
                                            key={value.name} value={value.name}>{value.name}</MenuItem>
                                    })}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button style={{color: "gray"}} onClick={closeDialog}>Abbrechen</Button>
                    <Button disabled={reportType === '' || reportFileType === ''} color={"primary"} onClick={createReport}>Report erstellen</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbarOpen} autoHideDuration={5000} onClose={closeSnackbar}
                      anchorOrigin={{vertical: "top", horizontal: "right"}} TransitionComponent={Grow}>
                <Alert onClose={closeSnackbar} severity={snackbarSeverity} sx={{width: '100%'}}>
                    {snackbarText}
                </Alert>
            </Snackbar>
        </Box>
    );
}
CoursesFilterAndReports.propTypes = {
    course: PropTypes.object.isRequired,
    applyFilters: PropTypes.func.isRequired,
};